import { FloorBased } from '@data/buildingProjects/DesignSchemes/FloorBased/FloorBased';
import { alphaShapeHull, alphaShapeHullLoops, simplifyCollinear } from '@data/geometry/ConcaveHull';
import FVector from '@data/geometry/FVector';
import {
    BorderMode,
    BorderSource,
    FacadeComponentType,
    FacadeDirectionMode,
    FacadeType,
    FloorBasedDesignState,
    FloorBasedFacadeState,
    LayerKind,
} from '@visualizer/types';
import { getViewer } from 'store/viewerStore';
import { getGrid } from 'store/LoadingStore';
import * as THREE from 'three';
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js';
import Grid from '../../../Geodata/grid';
import { SensorData } from '../../../Simulations/SensorPoint';
import { DesignSchemeRenderer } from '../DesignSchemeRenderer';
import { calculateCentroid, generatePointsCloud, getPerimeterPoints, removeDuplicatePoints, sortPointsCCW } from '../DesignSchemeUtils';
import { computeAverageDirectionFromSensors } from './SensorDirection';
import { Floor } from './components/Floor';

export class FloorBasedRenderer extends DesignSchemeRenderer {
    private floorBased: FloorBased;
    declare protected config: FloorBasedDesignState;

    constructor(floorBasedScheme: FloorBased, config: FloorBasedDesignState) {
        super(config);
        this.floorBased = floorBasedScheme;
    }

    public updateConfig(newConfig: Partial<FloorBasedDesignState>) {
        this.config = this.cloneConfig({ ...this.config, ...newConfig } as FloorBasedDesignState);
    }

    // Filter sensors by z-range:
    private getSensorsInRange(sensors: SensorData[], zMin: number, zMax: number): SensorData[] {
        return sensors.filter((s) => {
            const pos = s.getPosition();
            return pos.z >= zMin && pos.z <= zMax;
        });
    }

    // Get the loops (perimeters if there are more than one in the same floor. e.g. V Shape building):
    private getBorderLoops(
        source: BorderSource,
        sensors: SensorData[],
        elev: number,
        thickness: number,
        concave: boolean,
        alpha: number,
        role: 'single' | 'external' | 'internal'
    ) {
        // Compute Border loops based on the selected source:
        if (source === BorderSource.FOOTPRINT) return [this.floorBased.getFootprint()];

        if (source === BorderSource.BUILDABLE_BORDER)
            return [Grid.getBuildableBorderPoints().map((point) => new FVector(point.x, point.y, elev))];

        if (source === BorderSource.PROJECT_SKETCH) {
            const sketchIndex = this.config.borderState.projectSketchIndex ?? 0;
            const contour = getGrid().getActiveGeodata().dataObject.Buildings?.getProjectSketchContourPoints(elev, sketchIndex) ?? [];
            if (contour.length >= 3) return [contour];
        }

        if (source === BorderSource.HAND_DRAWN) {
            const handDrawn =
                role === 'external'
                    ? (this.config.borderState.handDrawnExternalBorder ?? [])
                    : role === 'internal'
                      ? (this.config.borderState.handDrawnInternalBorder ?? [])
                      : (this.config.borderState.handDrawnBorder ?? []);
            if (handDrawn.length >= 3) {
                return [handDrawn.map((point) => new FVector(point.x, point.y, elev))];
            }
            return [this.floorBased.getFootprint()];
        }

        if (source === BorderSource.BOX) return [this.computeBoxPerimeterFromSensors(sensors, elev, this.config.boxAlpha.value)];

        // Compute Concanve or Convex hull floor loops, from Main Layer sensors:
        if (concave) return this.computeConcaveLoopsFromSensors(sensors, elev, alpha);
        return [this.computeConvexPerimeterFromSensors(sensors, elev, thickness)];
    }

    private getFloorElevations(mainSensors: SensorData[]): { elevations: number[]; floorHeight: number; groundFloorHeight: number } {
        const { thickness, voxelFloors } = this.config;
        const floorHeight = voxelFloors ? Grid.gridRes : Math.max(this.config.floorHeight.value, Grid.gridRes);
        const groundFloorHeight = voxelFloors ? Grid.gridRes : Math.max(this.config.groundFloorHeight.value, Grid.gridRes);

        // Unlinked Mode: elevationSensors are not only the filtered ones (mainSensors) but all sensors inside footprint (baseSensors):
        let elevationSensors = mainSensors;
        if (!this.isLinkedToMainLayer()) {
            const unfilteredMain = this.floorBased.getUnfilteredSensorDatasFor(LayerKind.MAIN);

            // Keep the sensors inside the footprint (no matter if active or not):
            if (unfilteredMain.length > 0) {
                const baseSensors = this.floorBased.runPipeline(unfilteredMain, this.floorBased.getMinMaxFor(LayerKind.MAIN)).data;
                if (baseSensors.length > 0) elevationSensors = baseSensors;
            }
        }

        const maxZFromSensors = Math.max(...elevationSensors.map((s) => s.getPosition().z)) + Grid.gridRes / 2;
        const maxZ = this.config.buildingHeight?.value ?? maxZFromSensors;
        const minZ = Math.min(...elevationSensors.map((s) => s.getPosition().z)) - Grid.gridRes / 2;
        const elevations: number[] = [];

        let elevation = minZ;
        let ceilingElevation = elevation + groundFloorHeight + thickness.value * 2;
        while (elevation <= maxZ && ceilingElevation <= maxZ) {
            elevations.push(elevation);
            const h = elevations.length === 1 ? groundFloorHeight : floorHeight;
            elevation += h + thickness.value;
            ceilingElevation += floorHeight + thickness.value;
        }

        return { elevations, floorHeight, groundFloorHeight };
    }

    private isLinkedToMainLayer(): boolean {
        return this.config.linkToMainLayer ?? true;
    }

    private createFloors(
        mainSensors: SensorData[],
        facadeSensors: SensorData[],
        componentSensorsByType: Partial<Record<FacadeComponentType, SensorData[]>>,
        facadeState: FloorBasedFacadeState
    ): Floor[] {
        const { thickness, concaveAlpha, concave, borderState } = this.config;
        const { elevations, floorHeight, groundFloorHeight } = this.getFloorElevations(mainSensors);
        const facadeDirection =
            facadeState.directionMode === FacadeDirectionMode.VOLUME_AVERAGE
                ? computeAverageDirectionFromSensors(facadeSensors)
                : undefined;
        const componentDirectionByType = Object.fromEntries(
            Object.entries(componentSensorsByType).map(([componentType, sensors]) => [
                Number(componentType) as FacadeComponentType,
                (facadeState.componentLink && facadeState.directionMode === FacadeDirectionMode.VOLUME_AVERAGE) ||
                (!facadeState.componentLink && facadeState.componentDirectionMode === FacadeDirectionMode.VOLUME_AVERAGE)
                    ? computeAverageDirectionFromSensors(sensors ?? [])
                    : undefined,
            ])
        ) as Partial<Record<FacadeComponentType, FVector | undefined>>;

        let floorIndex = 0;
        const floors: Floor[] = [];
        let previousFloor: Floor | undefined;

        for (const elev of elevations) {
            const isGround = floorIndex === 0;
            const height = isGround ? groundFloorHeight : floorHeight;

            const zMin = elev;
            const zMax = elev + thickness.value + height;
            const facadeFloorSensors = this.getSensorsInRange(facadeSensors, zMin, zMax);
            const componentFloorSensorsByType = Object.fromEntries(
                Object.entries(componentSensorsByType).map(([componentType, sensors]) => [
                    componentType,
                    this.getSensorsInRange(sensors ?? [], zMin, zMax),
                ])
            ) as Partial<Record<FacadeComponentType, SensorData[]>>;
            const floorSensors = this.getSensorsInRange(mainSensors, zMin, zMax);
            if (this.isLinkedToMainLayer() && !floorSensors.length) {
                floorIndex++;
                continue;
            }

            const internalBorderSource = (
                borderState.mode === BorderMode.SINGLE ? borderState.single : borderState.internal
            ) as BorderSource;
            const internalLoops = this.getBorderLoops(
                internalBorderSource,
                floorSensors,
                elev,
                thickness.value,
                concave,
                concaveAlpha.value,
                borderState.mode === BorderMode.SINGLE ? 'single' : 'internal'
            );

            let externalLoops: FVector[][] | undefined;
            if (borderState.mode === BorderMode.DUAL) {
                externalLoops = this.getBorderLoops(
                    borderState.external as BorderSource,
                    floorSensors,
                    elev,
                    thickness.value,
                    concave,
                    concaveAlpha.value,
                    'external'
                );
            }

            const floor = new Floor(
                facadeFloorSensors,
                componentFloorSensorsByType,
                facadeState,
                facadeDirection,
                componentDirectionByType,
                internalLoops,
                externalLoops,
                height,
                thickness.value,
                elev,
                previousFloor
            );
            previousFloor?.setNextFloor(floor);
            floors.push(floor);
            previousFloor = floor;
            floorIndex++;
        }

        return floors;
    }

    // Used for the FACADE layer that needs it for the CloseToDS block:
    public buildFacadeShellGroup(mainSensors: SensorData[]): THREE.Group {
        // 1) Define the correct facadeState for an opaqueFacade:
        const facadeState =
            typeof structuredClone === 'function'
                ? structuredClone(this.config.facadeState)
                : (JSON.parse(JSON.stringify(this.config.facadeState)) as FloorBasedFacadeState);
        facadeState.type = FacadeType.OPAQUE;
        facadeState.hide = false;

        // 2) Setup Floors with opaque facades:
        const floors = this.createFloors(mainSensors, [], {}, facadeState);
        const shellGroup = new THREE.Group();

        // 3) Build the shell floors:
        for (const floor of floors) {
            const floorGroup = floor.build();
            const facadesGroup = floorGroup.children[2];
            if (facadesGroup) shellGroup.add(facadesGroup);
        }

        // Done:
        shellGroup.updateMatrixWorld(true);
        return shellGroup;
    }

    // Actual mesh generation:
    public async generateMesh() {
        const { facadeState, borderState } = this.config;

        if (borderState.single) {
            if (borderState.single == BorderSource.FOOTPRINT) this.floorBased.getFootprint();
        }

        this.meshGroup.clear();
        const scene = getViewer().getScene();

        const sensors = this.floorBased.getSensorDatasFor(LayerKind.MAIN);
        const facadeSensors = this.floorBased.getSensorDatasFor(LayerKind.FACADE);
        const defaultComponentLayerId = this.floorBased.getLayer(LayerKind.COMPONENT)?.getID();
        const componentSensorsByType: Partial<Record<FacadeComponentType, SensorData[]>> = {};
        facadeState.componentTypes.forEach((componentType) => {
            const layerId = facadeState.componentLayerIds[componentType] ?? defaultComponentLayerId;
            if (!layerId) {
                componentSensorsByType[componentType] = [];
                return;
            }
            componentSensorsByType[componentType] = this.floorBased.getComponentSensorsForLayer(layerId);
        });
        const floors = this.createFloors(sensors, facadeSensors, componentSensorsByType, facadeState);
        if (this.shouldAbortGeneration()) return;

        for (const floor of floors) {
            this.meshGroup.add(floor.build());
        }

        scene.add(this.meshGroup);
    }

    /**
     * Computes a 2D convex perimeter from the sensor centers.
     * Sets all Z coordinates to the elevation of this floor.
     */
    private computeConvexPerimeterFromSensors(sensors: SensorData[], elevation: number, thickness: number): FVector[] {
        if (sensors.length < 1) return [];

        // 1. Flatten sensor centers
        const points = generatePointsCloud(sensors);
        const uniquePoints = removeDuplicatePoints(points);

        // 2. Build convex hull (upper + lower) to give ConvexGeometry thickness
        const upper = uniquePoints.map((p) => new FVector(p.x, p.y, elevation + thickness));
        const lower = uniquePoints.map((p) => new FVector(p.x, p.y, elevation));
        const combined = [...upper, ...lower];
        const geom = new ConvexGeometry(combined.map((p) => new THREE.Vector3(p.x, p.y, p.z)));

        // 3. Extract perimeter from geometry
        let perimeter = getPerimeterPoints(geom);

        // 4. Flatten Z and sort
        perimeter = perimeter.map((p) => new FVector(p.x, p.y, elevation));
        const centroid = calculateCentroid(perimeter);
        perimeter = sortPointsCCW(perimeter, centroid);
        perimeter = removeDuplicatePoints(perimeter);

        return perimeter;
    }

    /**
     * Computes a 2D concave perimeter from the sensor centers.
     * Sets all Z coordinates to the elevation of this floor.
     */
    private computeConcavePerimeterFromSensors(sensors: SensorData[], elevation: number, concaveAlpha: number): FVector[] {
        if (sensors.length < 1) return [];

        // 1. Flatten sensor centers:
        const points = generatePointsCloud(sensors);
        const uniquePoints = removeDuplicatePoints(points);

        // 2. Build concave hull:
        const perimeter = alphaShapeHull(uniquePoints, concaveAlpha);
        const simplified = simplifyCollinear(perimeter);

        // 4. Flatten Z:
        return simplified.map((p) => new FVector(p.x, p.y, elevation));
    }

    /**
     * Computes the concave loops of a Floor based on the sensors inside of said floor. E.G. in a V Shaped building
     * this will return 2 loops for the upper floors as there will be effectively two towers.
     *
     * @param sensors
     * @param elevation
     * @param concaveAlpha
     *
     * @returns FVector[][] representing the floors loops.
     */
    private computeConcaveLoopsFromSensors(sensors: SensorData[], elevation: number, concaveAlpha: number): FVector[][] {
        if (sensors.length < 1) return [];

        const points = generatePointsCloud(sensors);
        const uniquePoints = removeDuplicatePoints(points);

        const loops = alphaShapeHullLoops(uniquePoints, concaveAlpha)
            .map((loop) => simplifyCollinear(loop))
            .filter((loop) => loop.length >= 3);

        if (!loops.length) return [this.computeConcavePerimeterFromSensors(sensors, elevation, concaveAlpha)];
        return loops.map((loop) => loop.map((point) => new FVector(point.x, point.y, elevation)));
    }

    /**
     * Computes a rotated 2D axis-aligned bounding rectangle in alpha-space.
     * The same configured alpha is used for all floors.
     */
    private computeBoxPerimeterFromSensors(sensors: SensorData[], elevation: number, alphaDeg: number): FVector[] {
        if (sensors.length < 1) return [];

        const points = removeDuplicatePoints(generatePointsCloud(sensors));
        if (points.length < 1) return [];

        const alphaRad = THREE.MathUtils.degToRad(alphaDeg);
        const cosNeg = Math.cos(-alphaRad);
        const sinNeg = Math.sin(-alphaRad);

        let minXr = Number.POSITIVE_INFINITY;
        let maxXr = Number.NEGATIVE_INFINITY;
        let minYr = Number.POSITIVE_INFINITY;
        let maxYr = Number.NEGATIVE_INFINITY;

        for (const p of points) {
            const xr = p.x * cosNeg - p.y * sinNeg;
            const yr = p.x * sinNeg + p.y * cosNeg;
            minXr = Math.min(minXr, xr);
            maxXr = Math.max(maxXr, xr);
            minYr = Math.min(minYr, yr);
            maxYr = Math.max(maxYr, yr);
        }

        const cosPos = Math.cos(alphaRad);
        const sinPos = Math.sin(alphaRad);
        const cornersRotated: Array<[number, number]> = [
            [minXr, minYr],
            [maxXr, minYr],
            [maxXr, maxYr],
            [minXr, maxYr],
        ];

        return cornersRotated.map(([xr, yr]) => {
            const x = xr * cosPos - yr * sinPos;
            const y = xr * sinPos + yr * cosPos;
            return new FVector(x, y, elevation);
        });
    }
}
